import { useCallback, useRef } from 'react';

/**
 * Every mounted post video element on the page. Module-level (not a context) so
 * players in unrelated trees — a feed card and the composer's preview lightbox —
 * still know about each other without a provider wrapping both.
 */
const mountedVideos = new Set<HTMLVideoElement>();

/**
 * Gives a `<video>` exclusive playback: starting it pauses every other post
 * video that is currently playing, so the feed never plays two clips at once.
 *
 * Spread the returned handlers onto the element:
 * `<video ref={registerVideo} onPlay={handlePlay} />`
 */
export const useExclusiveVideoPlayback = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // React calls this with `null` on unmount, which is where the video leaves the
  // registry — otherwise pausing others would touch detached elements.
  const registerVideo = useCallback((node: HTMLVideoElement | null) => {
    if (videoRef.current) {
      mountedVideos.delete(videoRef.current);
    }
    if (node) {
      mountedVideos.add(node);
    }
    videoRef.current = node;
  }, []);

  const handlePlay = useCallback(() => {
    mountedVideos.forEach(video => {
      if (video !== videoRef.current && !video.paused) {
        video.pause();
      }
    });
  }, []);

  return { registerVideo, handlePlay };
};
